import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SubjectFilterBar from '../../components/feature/SubjectFilterBar';
import { supabase } from '../../lib/supabase';
import QuizCreate from './components/QuizCreate';
import QuizPlay from './components/QuizPlay';
import QuizList from './components/QuizList';
import QuizStartConfig from './components/QuizStartConfig';
import type { MasteryFilter } from './components/QuizStartConfig';
import MasteryChart from './components/MasteryChart';
import BookmarkBreakdown from './components/BookmarkBreakdown';
import ReviewRanking from './components/ReviewRanking';
import AuthModal from '../../components/feature/AuthModal';
import ReviewReminder from '../../components/feature/ReviewReminder';
import SubjectManagerModal from '../../components/feature/SubjectManagerModal';
import { useSubjectList } from '../../hooks/useSubjectList';
import { getMasteryRecord } from '../../hooks/useQuizMastery';
import { fetchMasteryFromDB, type MasteryRecord } from '../../hooks/useQuizMasteryDB';
import { useReviewReminder } from '../../hooks/useReviewReminder';
import { useReviewRanking } from '../../hooks/useReviewRanking';
import {
  updatePageSEO,
  createBreadcrumbSchema,
  createPageSchema,
  createEducationalContentSchema,
  cleanupDynamicSchemas,
} from '../../utils/seo';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
  question: string;
  answer: string;
  choices: string[];
  explanation: string | null;
  created_at: string;
  user_id?: string | null;
}

const ADMIN_KEY = 'nagayama_quiz_admin';

export default function QuizPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { subjects: allSubjectNames, refreshSubjects } = useSubjectList();
  const [allQuestions, setAllQuestions] = useState<QuizQuestion[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'mine'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [playMode, setPlayMode] = useState(false);
  const [playQuestions, setPlayQuestions] = useState<QuizQuestion[]>([]);
  const [showStartConfig, setShowStartConfig] = useState(false);
  const [startConfigInitialFilter, setStartConfigInitialFilter] = useState<MasteryFilter | undefined>(undefined);
  const [masteryVersion, setMasteryVersion] = useState(0);
  const [masteryRecord, setMasteryRecord] = useState<MasteryRecord>({});
  const [playInitialBookmarks, setPlayInitialBookmarks] = useState<Set<string>>(new Set());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => sessionStorage.getItem(ADMIN_KEY) === 'true');
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [showSubjectManager, setShowSubjectManager] = useState(false);

  const { recommendedQuestions, totalCount, subjectBreakdown } = useReviewReminder(allQuestions, masteryRecord);
  const { ranking } = useReviewRanking(allQuestions, masteryRecord);

  // ─── SEO（マウント時1回のみ） ────────────────────────────────────────────
  useEffect(() => {
    updatePageSEO({
      title: '一問一答クイズ｜永山の勉強アーカイブ - 学習内容の復習クイズ',
      description: '永山の勉強アーカイブの一問一答クイズページ。学習した内容を復習するための一問一答形式のクイズを科目別・カテゴリ別に提供しています。',
      keywords: '一問一答,クイズ,復習,永山の勉強アーカイブ,学習記録',
      canonical: 'https://nagayama-study-archive.readdy.ai/quiz',
      ogType: 'website',
    });
    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: '一問一答クイズ', url: 'https://nagayama-study-archive.readdy.ai/quiz' },
    ]);
    createPageSchema({ name: '一問一答クイズ', description: '学習した内容を復習するための一問一答形式のクイズ。', url: 'https://nagayama-study-archive.readdy.ai/quiz', type: 'WebPage' });
    createEducationalContentSchema({
      name: '一問一答クイズ - 永山の勉強アーカイブ',
      description: '資格試験対策の一問一答形式クイズ。',
      url: 'https://nagayama-study-archive.readdy.ai/quiz',
      subjects: ['組織論', 'マーケティング', '生産管理', '財務会計', '会計学', '租税法', '簿記論', '民法'],
      educationalLevel: '資格試験対策',
    });
    return () => { cleanupDynamicSchemas(); };
  }, []);

  // ─── 問題フェッチ（useCallbackでメモ化） ─────────────────────────────────
  const fetchAllQuestions = useCallback(async () => {
    try {
      setDataLoading(true);
      let query = supabase.from('quiz_questions').select('*').order('created_at', { ascending: false });
      if (viewMode === 'mine' && user) {
        query = query.eq('user_id', user.id);
      }
      const { data, error } = await query;
      if (error) { setAllQuestions([]); } else { setAllQuestions((data as QuizQuestion[]) ?? []); }
    } catch {
      setAllQuestions([]);
    } finally {
      setDataLoading(false);
    }
  }, [viewMode, user]);

  useEffect(() => {
    fetchAllQuestions();
  }, [fetchAllQuestions]);

  // ─── 習熟度データ（masteryVersionが変わったときのみ再取得） ──────────────
  useEffect(() => {
    if (user) {
      fetchMasteryFromDB().then((record) => setMasteryRecord(record));
    } else {
      const localRecord = getMasteryRecord();
      const converted: MasteryRecord = {};
      Object.entries(localRecord).forEach(([qId, count]) => {
        converted[qId] = { correct_count: typeof count === 'number' ? count : 0, incorrect_count: 0, last_incorrect_at: null, is_bookmarked: false };
      });
      setMasteryRecord(converted);
    }
  }, [user, masteryVersion]);

  // ─── フィルター済み問題（メモ化） ────────────────────────────────────────
  const filteredQuestions = useMemo(() => {
    let result = allQuestions;
    if (selectedSubject) result = result.filter((q) => q.subject === selectedSubject);
    if (selectedSubcategory) result = result.filter((q) => q.subcategory === selectedSubcategory);
    return result;
  }, [allQuestions, selectedSubject, selectedSubcategory]);

  const subjectCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allQuestions.forEach((q) => { counts[q.subject] = (counts[q.subject] || 0) + 1; });
    return counts;
  }, [allQuestions]);

  const subcategoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allQuestions.forEach((q) => { if (q.subcategory) counts[q.subcategory] = (counts[q.subcategory] || 0) + 1; });
    return counts;
  }, [allQuestions]);

  const currentSubcategories = useMemo(() => {
    if (!selectedSubject) return [];
    const subs = new Set<string>();
    allQuestions.forEach((q) => { if (q.subject === selectedSubject && q.subcategory) subs.add(q.subcategory); });
    return Array.from(subs).sort().map((name) => ({ name }));
  }, [selectedSubject, allQuestions]);

  const bookmarkedCount = useMemo(
    () => allQuestions.filter((q) => masteryRecord[q.id]?.is_bookmarked === true).length,
    [allQuestions, masteryRecord]
  );

  // ─── コールバック ────────────────────────────────────────────────────────
  const handleSelectSubject = useCallback((subject: string) => {
    setSelectedSubject(subject);
    setSelectedSubcategory('');
  }, []);

  const handleCreated = useCallback(() => {
    setShowCreate(false);
    fetchAllQuestions();
    refreshSubjects();
  }, [fetchAllQuestions, refreshSubjects]);

  const handleStartQuiz = useCallback((subject: string, subcategory: string, count: number, masteryFilter: MasteryFilter) => {
    let pool = [...allQuestions];
    if (subject) pool = pool.filter((q) => q.subject === subject);
    if (subcategory) pool = pool.filter((q) => q.subcategory === subcategory);
    if (masteryFilter === 'unlearned') pool = pool.filter((q) => !masteryRecord[q.id] || masteryRecord[q.id].correct_count === 0);
    else if (masteryFilter === 'learning') pool = pool.filter((q) => masteryRecord[q.id] && masteryRecord[q.id].correct_count > 0 && masteryRecord[q.id].correct_count < 3);
    else if (masteryFilter === 'mastered') pool = pool.filter((q) => masteryRecord[q.id] && masteryRecord[q.id].correct_count >= 2);
    else if (masteryFilter === 'incorrect') pool = pool.filter((q) => masteryRecord[q.id] && masteryRecord[q.id].incorrect_count > 0);
    else if (masteryFilter === 'bookmarked') pool = pool.filter((q) => masteryRecord[q.id]?.is_bookmarked === true);
    if (pool.length === 0) return;
    const shuffled = pool.sort(() => Math.random() - 0.5);
    const selected = count >= shuffled.length ? shuffled : shuffled.slice(0, count);
    setPlayQuestions(selected);
    setShowStartConfig(false);
    setStartConfigInitialFilter(undefined);
    setPlayInitialBookmarks(new Set(selected.filter((q) => masteryRecord[q.id]?.is_bookmarked === true).map((q) => q.id)));
    setPlayMode(true);
  }, [allQuestions, masteryRecord]);

  const handleStartBookmarkedReview = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    const pool = allQuestions.filter((q) => masteryRecord[q.id]?.is_bookmarked === true);
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setPlayQuestions(shuffled);
    setPlayInitialBookmarks(new Set(shuffled.map((q) => q.id)));
    setPlayMode(true);
  }, [user, allQuestions, masteryRecord]);

  const handleQuizFinished = useCallback(() => setMasteryVersion((v) => v + 1), []);

  const handleRetryIncorrect = useCallback((incorrectQuestions: QuizQuestion[]) => {
    setPlayMode(false);
    setTimeout(() => {
      setPlayQuestions([...incorrectQuestions].sort(() => Math.random() - 0.5));
      setPlayMode(true);
    }, 100);
  }, []);

  const handleAdminLogin = useCallback(() => {
    if (adminPassword === 'nagayama2025') {
      setIsAdmin(true);
      sessionStorage.setItem(ADMIN_KEY, 'true');
      setShowAdminModal(false);
      setAdminPassword('');
      setAdminError('');
    } else {
      setAdminError('パスワードが正しくありません');
    }
  }, [adminPassword]);

  const handleAdminLogout = useCallback(() => {
    setIsAdmin(false);
    sessionStorage.removeItem(ADMIN_KEY);
  }, []);

  const handleStartReviewRecommended = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    const questionIds = new Set(recommendedQuestions.map((r) => r.questionId));
    const pool = allQuestions.filter((q) => questionIds.has(q.id));
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setPlayQuestions(shuffled);
    setPlayInitialBookmarks(new Set(shuffled.filter((q) => masteryRecord[q.id]?.is_bookmarked === true).map((q) => q.id)));
    setPlayMode(true);
  }, [user, recommendedQuestions, allQuestions, masteryRecord]);

  const handleStartQuizByCategory = useCallback((subject: string, subcategory: string | null) => {
    if (!user) { setShowAuthModal(true); return; }
    let pool = allQuestions.filter((q) => q.subject === subject);
    if (subcategory) pool = pool.filter((q) => q.subcategory === subcategory);
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setPlayQuestions(shuffled);
    setPlayInitialBookmarks(new Set(shuffled.filter((q) => masteryRecord[q.id]?.is_bookmarked === true).map((q) => q.id)));
    setPlayMode(true);
  }, [user, allQuestions, masteryRecord]);

  const canCreateQuiz = isAdmin || (!!user && viewMode === 'mine');
  const canEditAsUser = !!user && viewMode === 'mine';

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden flex-1 flex flex-col">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex-1 flex flex-col justify-center py-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-question-answer-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">クイズ</h1>
              <p className="text-lg text-teal-100">クイズで知識を定着させ、理解度を確認します</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4"><i className="ri-file-list-line text-2xl text-white"></i></div>
                <h3 className="text-base font-semibold text-white mb-2">クイズ作成</h3>
                <p className="text-sm text-teal-100">オリジナルのクイズを簡単に作成</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4"><i className="ri-bar-chart-line text-2xl text-white"></i></div>
                <h3 className="text-base font-semibold text-white mb-2">習熟度管理</h3>
                <p className="text-sm text-teal-100">正答率から習熟度を自動分析</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4"><i className="ri-repeat-line text-2xl text-white"></i></div>
                <h3 className="text-base font-semibold text-white mb-2">復習機能</h3>
                <p className="text-sm text-teal-100">間違えた問題を重点的に復習</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg">
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
              <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm">
                <i className="ri-user-add-line mr-2"></i>新規登録
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900">
      <Header />

      <section className="pt-24 pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-orb-1 absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-3 absolute top-1/3 left-1/3 w-[350px] h-[350px] bg-teal-200/8 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-40 -right-40 w-[800px] h-[200px] bg-gradient-to-r from-transparent via-white/5 to-transparent rotate-[30deg] blur-2xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-sm text-teal-200 mb-6 animate-hero-fade-up">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">Top</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">一問一答クイズ</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 animate-hero-fade-up-delay">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">一問一答クイズ</h1>
              <p className="text-teal-100 text-sm sm:text-base max-w-2xl leading-relaxed">学習した内容を復習するための一問一答形式のクイズです。</p>
              <p className="text-teal-200/80 text-xs sm:text-sm max-w-2xl mt-1.5">
                ※ 内容に誤りが含まれている可能性があります。お気づきの際は<a href="/contact" className="underline hover:text-white cursor-pointer">お問い合わせ</a>よりご指摘ください。
              </p>
            </div>
            <div className="flex items-center gap-2 animate-hero-fade-up-delay2">
              <button onClick={() => setShowSubjectManager(true)}
                className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-white/15 text-white rounded-xl text-sm font-medium hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm border border-white/10 shadow-sm">
                <i className="ri-folder-settings-line text-base"></i>
                <span className="hidden sm:inline">科目・カテゴリ管理</span><span className="sm:hidden">管理</span>
              </button>
              {canCreateQuiz && (
                <button onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-white/15 text-white rounded-xl text-sm font-medium hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm border border-white/10 shadow-sm">
                  <i className="ri-add-line text-base"></i>
                  <span className="hidden sm:inline">クイズを作成</span><span className="sm:hidden">作成</span>
                </button>
              )}
              {isAdmin ? (
                <button onClick={handleAdminLogout} className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-white/15 text-white/70 rounded-xl text-xs font-medium hover:bg-white/20 hover:text-white transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm border border-white/10">
                  <i className="ri-lock-line text-sm"></i><span className="hidden sm:inline">管理終了</span>
                </button>
              ) : (
                <button onClick={() => setShowAdminModal(true)} className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-white/10 text-white/70 rounded-xl text-xs font-medium hover:bg-white/20 hover:text-white transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm border border-white/10">
                  <i className="ri-lock-line text-sm"></i><span className="hidden sm:inline">管理者</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* タブ切り替え */}
      {user && (
        <section className="py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
              <button onClick={() => setViewMode('all')}
                className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'all' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}>
                <i className="ri-global-line mr-1 sm:mr-1.5"></i>全体
              </button>
              <button onClick={() => setViewMode('mine')}
                className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'mine' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}>
                <i className="ri-user-line mr-1 sm:mr-1.5"></i>自分
              </button>
            </div>
            {viewMode === 'mine' && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                <i className="ri-information-line mr-1 text-teal-500 dark:text-teal-400"></i>
                自分のタブでは、管理者パスワード不要でクイズの作成・編集・削除ができます
              </p>
            )}
          </div>
        </section>
      )}

      {/* Review Reminder */}
      {user && totalCount > 0 && (
        <section className="py-6 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ReviewReminder totalCount={totalCount} subjectBreakdown={subjectBreakdown} onStartReview={handleStartReviewRecommended} />
          </div>
        </section>
      )}

      {/* 復習優先度ランキング */}
      {user && ranking.length > 0 && (
        <section className="pb-6 sm:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ReviewRanking ranking={ranking} onStartQuizByCategory={handleStartQuizByCategory} />
          </div>
        </section>
      )}

      {/* クイズ開始カード */}
      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-3 sm:space-y-4">
          <div className="bg-gradient-to-r from-teal-500 via-teal-500 to-emerald-600 rounded-2xl p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 shadow-sm">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/15 rounded-xl shrink-0">
                <i className="ri-gamepad-line text-xl sm:text-2xl text-white"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-bold text-white mb-0.5">クイズに挑戦する</h3>
                <p className="text-xs text-teal-100">科目やカテゴリ、習熟度を選んで挑戦できます（現在{allQuestions.length}問登録済み）</p>
              </div>
            </div>
            <button onClick={() => { setStartConfigInitialFilter(undefined); setShowStartConfig(true); }} disabled={allQuestions.length === 0}
              className="w-full px-6 py-3 bg-white text-teal-700 rounded-xl text-sm font-bold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm">
              <i className="ri-play-fill text-base"></i>挑戦する
            </button>
          </div>

          {user && bookmarkedCount > 0 && (
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 shadow-sm">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/15 rounded-xl shrink-0">
                  <i className="ri-bookmark-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-bold text-white mb-0.5">苦手チェックをいれた問題を復習する</h3>
                  <p className="text-xs text-amber-100">苦手チェック済みの <span className="font-bold text-white">{bookmarkedCount}問</span> を集中的に復習できます</p>
                </div>
              </div>
              <button onClick={handleStartBookmarkedReview}
                className="w-full px-6 py-3 bg-white text-orange-600 rounded-xl text-sm font-bold hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm">
                <i className="ri-bookmark-line text-base"></i>復習する
              </button>
            </div>
          )}

          {!user && (
            <div className="bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 shadow-sm">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-amber-50 dark:bg-amber-900/30 rounded-xl shrink-0">
                  <i className="ri-bookmark-line text-xl sm:text-2xl text-amber-500"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-bold text-gray-800 dark:text-white mb-0.5">苦手チェック機能 &amp; オリジナルクイズ作成</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">ログインすると、苦手な問題にチェックを入れたり、自分専用のクイズを作成できます</p>
                </div>
              </div>
              <button onClick={() => setShowAuthModal(true)}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm">
                <i className="ri-user-line text-base"></i>ログインする
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 習熟度チャート */}
      <section className="pb-6 sm:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <MasteryChart questions={allQuestions} refreshKey={masteryVersion} />
        </div>
      </section>

      {user && bookmarkedCount > 0 && (
        <section className="pb-6 sm:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <BookmarkBreakdown questions={allQuestions} masteryRecord={masteryRecord} />
          </div>
        </section>
      )}

      {/* フィルター */}
      <section className="pt-2 sm:pt-4 pb-2 bg-white dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <SubjectFilterBar
            subjects={allSubjectNames}
            selectedSubject={selectedSubject}
            onSelectSubject={handleSelectSubject}
            showCount={true}
            subjectCounts={subjectCounts}
            onRefresh={() => { refreshSubjects(); fetchAllQuestions(); }}
          />
        </div>
      </section>

      {currentSubcategories.length > 0 && (
        <section className="pb-3 sm:pb-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 sm:pb-4">
            <div className="mt-2 sm:mt-3 pl-2 sm:pl-3 border-l-2 border-teal-400 dark:border-teal-500">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 ml-1">
                <i className="ri-price-tag-3-line mr-1 text-teal-500 dark:text-teal-400"></i>{selectedSubject}のカテゴリ
              </label>
              <div className="flex flex-wrap gap-1.5 ml-1">
                <button onClick={() => setSelectedSubcategory('')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${selectedSubcategory === '' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'}`}>
                  すべて
                </button>
                {currentSubcategories.map((sub) => (
                  <button key={sub.name} onClick={() => setSelectedSubcategory(sub.name)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${selectedSubcategory === sub.name ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'}`}>
                    {sub.name}{subcategoryCounts[sub.name] ? ` (${subcategoryCounts[sub.name]})` : ''}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-white">
              {viewMode === 'mine' ? '自分のクイズ' : '登録済みの問題'}
              <span className="text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">{filteredQuestions.length}問</span>
            </h2>
          </div>
          <QuizList
            questions={filteredQuestions}
            loading={dataLoading}
            onRefresh={fetchAllQuestions}
            isAdmin={isAdmin || canEditAsUser}
            currentUserId={user?.id}
            userOwnedMode={viewMode === 'mine'}
            masteryRecord={masteryRecord}
          />
        </div>
      </section>

      {showCreate && canCreateQuiz && (
        <QuizCreate onCreated={handleCreated} onClose={() => setShowCreate(false)} userId={user?.id} />
      )}

      {showStartConfig && (
        <QuizStartConfig
          totalCount={allQuestions.length}
          questions={allQuestions}
          onStart={handleStartQuiz}
          onClose={() => { setShowStartConfig(false); setStartConfigInitialFilter(undefined); }}
          masteryRecord={masteryRecord}
          initialMasteryFilter={startConfigInitialFilter}
        />
      )}

      {playMode && playQuestions.length > 0 && (
        <QuizPlay
          questions={playQuestions}
          onClose={() => setPlayMode(false)}
          onFinished={handleQuizFinished}
          onRetryIncorrect={handleRetryIncorrect}
          isLoggedIn={!!user}
          initialBookmarks={playInitialBookmarks}
          onRequestLogin={() => { setPlayMode(false); setShowAuthModal(true); }}
        />
      )}

      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={() => { setShowAuthModal(false); setMasteryVersion((v) => v + 1); }} />
      )}

      {showAdminModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowAdminModal(false); setAdminPassword(''); setAdminError(''); }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 rounded-full mx-auto mb-3">
                <i className="ri-lock-line text-2xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">管理者ログイン</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">クイズの作成・編集・削除にはパスワードが必要です</p>
            </div>
            {adminError && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm rounded-xl px-4 py-2.5 mb-4">{adminError}</div>}
            <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()} placeholder="パスワードを入力"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white mb-4" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => { setShowAdminModal(false); setAdminPassword(''); setAdminError(''); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap">
                キャンセル
              </button>
              <button onClick={handleAdminLogin}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40">
                ログイン
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubjectManager && (
        <SubjectManagerModal
          onClose={() => setShowSubjectManager(false)}
          onUpdated={() => { refreshSubjects(); fetchAllQuestions(); }}
        />
      )}

      <Footer />
    </div>
  );
}
